(() => {
  const root = document.querySelector('[data-fp-search]');
  if (!root) return;

  const input = root.querySelector('[data-fp-search-input]');
  const closeButton = root.querySelector('[data-fp-search-close]');
  const items = root.querySelector('[data-fp-search-items]');
  const emptyState = root.querySelector('[data-fp-search-empty]');
  const loading = root.querySelector('[data-fp-search-loading]');
  const status = root.querySelector('[data-fp-search-status]');
  const popularList = root.querySelector('[data-fp-search-popular-list]');
  const toggle = document.querySelector('[data-fp-search-toggle]');

  if (!input || !items || !emptyState || !closeButton || !popularList || !toggle) return;

  const body = document.body;
  const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
  let isOpen = false;
  let debounceTimer = null;
  let abortController = null;
  let hasDynamicPopular = false;

  const popularIconMarkup = root.querySelector('.fp-search__popular-icon')?.innerHTML || '';

  const formatWithDelimiters = (number, precision, thousands, decimal) => {
    if (number == null || Number.isNaN(number)) return '';
    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return parts[1] ? `${dollars}${decimal}${parts[1]}` : dollars;
  };

  const formatMoney = (cents, format) => {
    if (cents == null) return '';
    let value = cents;
    if (typeof value === 'string') {
      value = value.replace(/[^0-9.-]/g, '');
    }
    value = Number(value);
    if (Number.isNaN(value)) return '';
    if (!Number.isInteger(value)) {
      value = Math.round(value * 100);
    }

    const formatString = format || '${{amount}}';
    const match = formatString.match(/\{\{\s*(\w+)\s*\}\}/);
    const formatType = match ? match[1] : 'amount';
    let formatted = '';

    switch (formatType) {
      case 'amount_no_decimals':
        formatted = formatWithDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = formatWithDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        formatted = formatWithDelimiters(value, 2, ' ', '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        formatted = formatWithDelimiters(value, 0, ' ', '.');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = formatWithDelimiters(value, 2, "'", '.');
        break;
      default:
        formatted = formatWithDelimiters(value, 2, ',', '.');
        break;
    }

    return match ? formatString.replace(match[0], formatted) : formatted;
  };

  const normalizeImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('//')) return `https:${url}`;
    return url;
  };

  const clearResults = () => {
    items.innerHTML = '';
    emptyState.hidden = true;
    if (status) status.textContent = '';
  };

  const setLoading = (state) => {
    if (!loading) return;
    loading.hidden = !state;
  };

  const openSearch = () => {
    if (isOpen) return;
    isOpen = true;
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    body.classList.add('fp-search-open');
    input.setAttribute('aria-expanded', 'true');
    toggle.setAttribute('aria-expanded', 'true');
    root.classList.remove('fp-search-panel-visible');
    document.addEventListener('click', handleOutsideClick);
    document.addEventListener('keydown', handleKeydown);
    requestAnimationFrame(() => input.focus());
  };

  const closeSearch = () => {
    if (!isOpen) return;
    isOpen = false;
    body.classList.remove('fp-search-open');
    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    input.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-expanded', 'false');
    root.classList.remove('fp-search-panel-visible');
    document.removeEventListener('click', handleOutsideClick);
    document.removeEventListener('keydown', handleKeydown);
    input.value = '';
    clearResults();
    if (abortController) abortController.abort();
  };

  const handleOutsideClick = (event) => {
    if (root.contains(event.target) || toggle.contains(event.target)) return;
    closeSearch();
  };

  const handleKeydown = (event) => {
    if (event.key === 'Escape' || event.key === 'Esc') {
      closeSearch();
    }
  };

  const renderPopular = (queries) => {
    if (!queries || queries.length === 0) return;
    popularList.innerHTML = '';
    queries.slice(0, 5).forEach((query) => {
      const term = query?.text || query?.styled_text || '';
      if (!term) return;
      const item = document.createElement('li');
      item.className = 'fp-search__popular-item';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'fp-search__popular-button';
      button.setAttribute('data-fp-search-popular', term);

      const icon = document.createElement('span');
      icon.className = 'fp-search__popular-icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = popularIconMarkup;

      const label = document.createElement('span');
      label.textContent = term;

      button.appendChild(icon);
      button.appendChild(label);
      item.appendChild(button);
      popularList.appendChild(item);
    });
    hasDynamicPopular = true;
  };

  const renderResults = (products) => {
    items.innerHTML = '';
    if (!products || products.length === 0) {
      emptyState.hidden = false;
      if (status) status.textContent = '0 results';
      root.classList.remove('fp-search-panel-visible');
      return;
    }

    emptyState.hidden = true;
    products.forEach((product) => {
      const item = document.createElement('li');
      item.className = 'fp-search__item';

      const link = document.createElement('a');
      link.className = 'fp-search__item-link';
      link.href = product.url || '#';

      const thumb = document.createElement('span');
      thumb.className = 'fp-search__item-thumb';

      const imageValue = product?.image;
      const imageUrl = normalizeImageUrl(
        typeof imageValue === 'string' ? imageValue : imageValue?.url || imageValue?.src
      );
      if (imageUrl) {
        const img = document.createElement('img');
        img.className = 'fp-search__item-image';
        img.src = imageUrl;
        img.alt = product?.title || '';
        img.loading = 'lazy';
        thumb.appendChild(img);
      }

      const text = document.createElement('span');
      text.className = 'fp-search__item-text';

      const title = document.createElement('span');
      title.className = 'fp-search__item-title';
      title.textContent = product?.title || '';

      const price = document.createElement('span');
      price.className = 'fp-search__item-price';
      const priceValue = product?.price || product?.price_min || '';
      price.textContent = formatMoney(priceValue, moneyFormat);

      text.appendChild(title);
      text.appendChild(price);

      link.appendChild(thumb);
      link.appendChild(text);

      item.appendChild(link);

      const variantId = product?.variants?.[0]?.id;
      if (variantId) {
        const addButton = document.createElement('button');
        addButton.type = 'button';
        addButton.className = 'fp-search__item-add';
        addButton.setAttribute('aria-label', `Add ${product?.title || 'item'} to cart`);
        addButton.setAttribute('data-fp-search-add', variantId);
        addButton.textContent = '+';
        item.appendChild(addButton);
      }

      items.appendChild(item);
    });

    if (status) status.textContent = `${products.length} results`;
  };

  const fetchResults = (term) => {
    const trimmedTerm = term.trim();
    if (!trimmedTerm) {
      clearResults();
      root.classList.remove('fp-search-panel-visible');
      setLoading(false);
      return;
    }

    if (abortController) abortController.abort();
    abortController = new AbortController();

    setLoading(trimmedTerm.length > 0);
    const params = new URLSearchParams({
      q: trimmedTerm,
      'resources[type]': 'product,query',
      'resources[limit]': '6',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,vendor,variants.sku',
    });

    fetch(`${window.routes?.predictive_search_url || '/search/suggest'}.json?${params.toString()}`, {
      signal: abortController.signal,
      headers: {
        Accept: 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        const products = data?.resources?.results?.products || [];
        const queries = data?.resources?.results?.queries || [];
        if (queries.length > 0 || !hasDynamicPopular) {
          renderPopular(queries);
        }
        if (trimmedTerm) {
          renderResults(products);
        }
        const shouldShowPanel = trimmedTerm.length > 0 && (products.length > 0 || queries.length > 0);
        root.classList.toggle('fp-search-panel-visible', shouldShowPanel);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        clearResults();
        root.classList.remove('fp-search-panel-visible');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleInput = () => {
    const term = input.value.trim();

    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      fetchResults(term);
    }, 200);
  };

  const handlePopularClick = (event) => {
    const button = event.target.closest('[data-fp-search-popular]');
    if (!button) return;
    const term = button.getAttribute('data-fp-search-popular');
    if (!term) return;
    input.value = term;
    input.focus();
    fetchResults(term);
    root.classList.add('fp-search-panel-visible');
  };

  const handleAddToCart = (event) => {
    const button = event.target.closest('[data-fp-search-add]');
    if (!button) return;
    const variantId = button.getAttribute('data-fp-search-add');
    if (!variantId || button.classList.contains('is-loading')) return;

    button.classList.add('is-loading');
    fetch(window.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: 1,
          },
        ],
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cart add failed');
        return response.json();
      })
      .then(() => {
        if (status) status.textContent = 'Added to cart';
      })
      .catch(() => {
        if (status) status.textContent = 'Unable to add to cart';
      })
      .finally(() => {
        button.classList.remove('is-loading');
      });
  };

  input.addEventListener('input', handleInput);
  closeButton.addEventListener('click', closeSearch);
  root.addEventListener('click', handleAddToCart);
  root.addEventListener('click', (event) => {
    event.stopPropagation();
  });
  popularList.addEventListener('click', handlePopularClick);
  toggle.addEventListener('click', (event) => {
    event.preventDefault();
    isOpen ? closeSearch() : openSearch();
  });
})();
